import React, { useState, useEffect } from 'react';
import './FineTuningManagement.css';
import FineTuningJobs from './FineTuningJobs';

const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '');

export default function FineTuningManagement() {
  const [activeSubTab, setActiveSubTab] = useState('data'); // 'data' or 'jobs'
  const [validations, setValidations] = useState([]);
  const [stats, setStats] = useState(null);
  const [datasetStats, setDatasetStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    documentType: '',
    approved: '',
    exported: ''
  });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0
  });

  // 🏷️ Dataset Tab State
  const [dataset, setDataset] = useState([]);
  const [datasetLoading, setDatasetLoading] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState(null);

  // Load data on mount
  useEffect(() => {
    if (activeSubTab === 'data') {
      loadValidations();
      loadStats();
      loadDatasetStats();
    } else if (activeSubTab === 'dataset') {
      loadDataset();
    }
  }, [activeSubTab, filters, pagination.offset]);

  const loadValidations = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset,
        ...(filters.documentType && { documentType: filters.documentType }),
        ...(filters.approved !== '' && { approved: filters.approved }),
        ...(filters.exported !== '' && { exported: filters.exported })
      });

      const response = await fetch(`${API_BASE_URL}/api/finetuning/validations?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setValidations(data.data);
        setPagination(prev => ({ ...prev, total: data.total }));
      }
    } catch (error) {
      console.error('Failed to load validations:', error);
      alert('검증 이력을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/finetuning/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadDatasetStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/finetuning/dataset/stats`);
      const data = await response.json();
      if (data.success) {
        setDatasetStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load dataset stats:', error);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(validations.map(v => v.validation_id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleApproveSelected = async (approved) => {
    if (selectedIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }

    const quality = approved ? prompt('품질 등급을 입력하세요 (excellent/good/fair):', 'good') : null;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/finetuning/validations/approve-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          validationIds: selectedIds,
          approved,
          quality,
          notes: approved ? '일괄 승인' : '일괄 거부'
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`${data.successCount}개 항목이 ${approved ? '승인' : '거부'}되었습니다.`);
        setSelectedIds([]);
        loadValidations();
        loadStats();
      }
    } catch (error) {
      console.error('Failed to approve:', error);
      alert('승인 처리에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToJsonl = async () => {
    if (selectedIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }

    const confirmed = window.confirm(
      `선택된 ${selectedIds.length}개 항목을 JSONL로 생성하여 GCS에 업로드하시겠습니까?\n` +
      `이 작업은 파인튜닝 데이터셋에 추가됩니다.`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/finetuning/export-to-jsonl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ validationIds: selectedIds })
      });

      const data = await response.json();

      if (data.success) {
        alert(
          `✅ JSONL 생성 완료!\n\n` +
          `성공: ${data.successCount}개\n` +
          `실패: ${data.failedCount}개\n` +
          `위치: ${data.datasetLocation}`
        );
        setSelectedIds([]);
        loadValidations();
        loadStats();
        loadDatasetStats();
      } else {
        alert(`❌ 실패: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to export:', error);
      alert('JSONL 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/finetuning/validation/${id}`);
      const data = await response.json();

      if (data.success) {
        const validation = data.data;
        const rawResult = JSON.parse(validation.ocr_raw_result || '{}');
        const validatedResult = JSON.parse(validation.validated_result || '{}');

        const detailText = `
📋 검증 이력 상세 정보

ID: ${validation.validation_id}
문서 타입: ${validation.document_type}
사용자: ${validation.user_id} (그룹: ${validation.group_id})
차량: ${validation.car_name || 'N/A'} (${validation.car_number || 'N/A'})
생성일: ${new Date(validation.created_at).toLocaleString('ko-KR')}

승인 상태: ${validation.is_approved_for_training ? '✅ 승인됨' : '❌ 미승인'}
품질 등급: ${validation.training_quality || 'N/A'}
JSONL 생성: ${validation.is_exported_to_jsonl ? '✅ 완료' : '❌ 미생성'}

PDF 위치: ${validation.pdf_gcs_uri || validation.pdf_local_path || 'N/A'}

OCR 원본 결과:
${JSON.stringify(rawResult, null, 2)}

검증된 최종 결과:
${JSON.stringify(validatedResult, null, 2)}

메모: ${validation.notes || 'N/A'}
        `;

        alert(detailText);
      }
    } catch (error) {
      console.error('Failed to load detail:', error);
      alert('상세 정보를 불러오는데 실패했습니다.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getQualityStars = (quality) => {
    if (!quality) return '-';
    const stars = {
      'excellent': '⭐⭐⭐',
      'good': '⭐⭐',
      'fair': '⭐'
    };
    return stars[quality] || quality;
  };

  // 🏷️ Dataset Logic
  const loadDataset = async () => {
    try {
      setDatasetLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/learning/dataset`);
      const data = await response.json();
      if (data.success) {
        setDataset(data.data);
      }
    } catch (error) {
      console.error('Failed to load dataset:', error);
    } finally {
      setDatasetLoading(false);
    }
  };

  const generateDataset = async () => {
    if (!window.confirm('기존 데이터를 바탕으로 학습용 데이터셋(Crop Image)을 생성하시겠습니까?')) return;

    try {
      setDatasetLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/learning/generate-dataset`, { method: 'POST' });
      const data = await response.json();

      if (data.success) {
        alert(data.message);
        loadDataset(); // Reload list
      } else {
        alert('생성 실패: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to generate dataset:', error);
      alert('데이터셋 생성 요청 실패');
    } finally {
      setDatasetLoading(false);
    }
  };

  const startTraining = async () => {
    if (!window.confirm('PaddleOCR 학습을 시작하시겠습니까? (백그라운드 실행)')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/learning/train`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        alert(data.message);
        setTrainingStatus('Training started...');
      }
    } catch (error) {
      alert('학습 시작 실패');
    }
  };

  return (
    <div className="finetuning-management">
      <div className="ft-header">
        <h1>파인튜닝 관리</h1>
        <p>검증된 OCR 결과를 관리하고 Vertex AI 파인튜닝 작업을 생성하세요</p>
      </div>

      {/* Sub Tabs */}
      <div className="ft-sub-tabs">
        <button
          className={`ft-sub-tab ${activeSubTab === 'data' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('data')}
        >
          검증 데이터 관리
        </button>
        <button
          className={`ft-sub-tab ${activeSubTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('jobs')}
        >
          파인튜닝 작업
        </button>
        <button
          className={`ft-sub-tab ${activeSubTab === 'dataset' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('dataset')}
        >
          학습 데이터셋 (PaddleOCR)
        </button>
      </div>

      {activeSubTab === 'jobs' ? (
        <FineTuningJobs />
      ) : activeSubTab === 'dataset' ? (
        <div className="ft-dataset-container" style={{ padding: '20px' }}>
          {/* Action Header */}
          <div className="flex justify-between items-center mb-4 bg-white p-4 rounded shadow-sm border border-gray-200">
            <div>
              <h2 className="text-lg font-bold text-gray-800">PaddleOCR 학습 데이터셋</h2>
              <p className="text-sm text-gray-500">라벨링된 데이터에서 추출한 Crop 이미지 목록입니다. ({dataset.length}개)</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={generateDataset}
                disabled={datasetLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-bold text-sm flex items-center gap-2"
              >
                {datasetLoading ? '생성 중...' : '📸 데이터셋 재생성 (Crop)'}
              </button>
              <button
                onClick={startTraining}
                className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 font-bold text-sm flex items-center gap-2"
              >
                🚀 학습 시작
              </button>
            </div>
          </div>

          {/* Dataset Grid */}
          {datasetLoading ? (
            <div className="text-center py-20 text-gray-500">데이터를 불러오는 중...</div>
          ) : dataset.length === 0 ? (
            <div className="text-center py-20 bg-gray-50 rounded border border-dashed border-gray-300">
              <p className="text-gray-500 mb-2">생성된 학습 데이터가 없습니다.</p>
              <button onClick={generateDataset} className="text-blue-600 underline font-medium">데이터셋 생성하기</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {dataset.map((item, idx) => (
                <div key={idx} className="bg-white border border-gray-200 rounded p-2 flex flex-col items-center hover:shadow-md transition-shadow">
                  <div className="h-16 w-full flex items-center justify-center bg-gray-50 mb-2 rounded overflow-hidden">
                    {/* API_BASE_URL prefix handled in src if needed, but item.image starts with /training-images */}
                    <img
                      src={`${API_BASE_URL}${item.image}`}
                      alt="crop"
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="w-full text-center">
                    <code className="text-xs font-mono bg-gray-100 px-1 py-0.5 rounded text-gray-700 block truncate" title={item.label}>
                      {item.label}
                    </code>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="ft-data-management">

            {/* Statistics Dashboard */}
            <div className="ft-stats-dashboard">
              <div className="ft-stat-card">
                <div className="ft-stat-label">총 검증 데이터</div>
                <div className="ft-stat-value">{stats?.total || 0}개</div>
              </div>
              <div className="ft-stat-card">
                <div className="ft-stat-label">승인됨</div>
                <div className="ft-stat-value approved">{stats?.approved || 0}개</div>
              </div>
              <div className="ft-stat-card">
                <div className="ft-stat-label">JSONL 생성됨</div>
                <div className="ft-stat-value exported">{stats?.exported || 0}개</div>
              </div>
              <div className="ft-stat-card">
                <div className="ft-stat-label">GCS 데이터셋</div>
                <div className="ft-stat-value dataset">
                  {datasetStats?.exists ? `${datasetStats.entryCount}개 항목` : '없음'}
                </div>
                {datasetStats?.exists && (
                  <div className="ft-stat-detail">
                    {(datasetStats.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="ft-filters">
              <select
                value={filters.documentType}
                onChange={(e) => setFilters(prev => ({ ...prev, documentType: e.target.value }))}
              >
                <option value="">전체 문서타입</option>
                <option value="flash">Flash</option>
                <option value="pro">Pro</option>
                <option value="invoice">Invoice</option>
              </select>

              <select
                value={filters.approved}
                onChange={(e) => setFilters(prev => ({ ...prev, approved: e.target.value }))}
              >
                <option value="">전체 승인상태</option>
                <option value="true">승인됨</option>
                <option value="false">미승인</option>
              </select>

              <select
                value={filters.exported}
                onChange={(e) => setFilters(prev => ({ ...prev, exported: e.target.value }))}
              >
                <option value="">전체 JSONL 상태</option>
                <option value="true">생성됨</option>
                <option value="false">미생성</option>
              </select>

              <button onClick={loadValidations} disabled={loading}>
                새로고침
              </button>
            </div>

            {/* Actions */}
            <div className="ft-actions">
              <div className="ft-actions-left">
                <span className="ft-selected-count">
                  {selectedIds.length}개 선택됨
                </span>
              </div>
              <div className="ft-actions-right">
                <button
                  onClick={() => handleApproveSelected(true)}
                  disabled={selectedIds.length === 0 || loading}
                  className="btn-approve"
                >
                  선택 항목 승인
                </button>
                <button
                  onClick={() => handleApproveSelected(false)}
                  disabled={selectedIds.length === 0 || loading}
                  className="btn-reject"
                >
                  선택 항목 거부
                </button>
                <button
                  onClick={handleExportToJsonl}
                  disabled={selectedIds.length === 0 || loading}
                  className="btn-export"
                >
                  JSONL 생성
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="ft-table-container">
              {loading && <div className="ft-loading">로딩 중...</div>}

              <table className="ft-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedIds.length === validations.length && validations.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>ID</th>
                    <th>날짜</th>
                    <th>문서타입</th>
                    <th>차량명</th>
                    <th>차량번호</th>
                    <th>사용자</th>
                    <th>품질</th>
                    <th>승인</th>
                    <th>JSONL</th>
                    <th>액션</th>
                  </tr>
                </thead>
                <tbody>
                  {validations.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="ft-no-data">
                        데이터가 없습니다
                      </td>
                    </tr>
                  ) : (
                    validations.map(v => (
                      <tr key={v.validation_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(v.validation_id)}
                            onChange={() => handleSelectOne(v.validation_id)}
                          />
                        </td>
                        <td>{v.validation_id}</td>
                        <td className="ft-date">{formatDate(v.created_at)}</td>
                        <td>
                          <span className={`ft-badge ft-badge-${v.document_type}`}>
                            {v.document_type}
                          </span>
                        </td>
                        <td>{v.car_name || '-'}</td>
                        <td>{v.car_number || '-'}</td>
                        <td>{v.user_id}</td>
                        <td>{getQualityStars(v.training_quality)}</td>
                        <td>
                          {v.is_approved_for_training ? (
                            <span className="ft-status approved">✅</span>
                          ) : (
                            <span className="ft-status pending">⏳</span>
                          )}
                        </td>
                        <td>
                          {v.is_exported_to_jsonl ? (
                            <span className="ft-status exported">✅</span>
                          ) : (
                            <span className="ft-status not-exported">❌</span>
                          )}
                        </td>
                        <td>
                          <button
                            onClick={() => handleViewDetail(v.validation_id)}
                            className="btn-view"
                          >
                            보기
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="ft-pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                disabled={pagination.offset === 0 || loading}
              >
                ◀ 이전
              </button>
              <span className="ft-page-info">
                {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} / {pagination.total}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                disabled={pagination.offset + pagination.limit >= pagination.total || loading}
              >
                다음 ▶
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

